import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  useTheme,
  Fade,
  Chip,
  Stack,
  Badge,
} from "@mui/material";
import { styled } from "@mui/system";

export type BingoCell = { number: number; isClicked: boolean };

export const BINGO = ["B", "I", "N", "G", "O"] as const;

/* -------------------------- Helpers & Styling -------------------------- */

export const getShuffledBoard = (): BingoCell[] => {
  const nums = Array.from({ length: 25 }, (_, i) => i + 1);
  for (let i = nums.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [nums[i], nums[j]] = [nums[j], nums[i]];
  }
  return nums.map((num) => ({ number: num, isClicked: false }));
};

// normalize "bingo-session-created" | "bingo:session-created" → "bingo:session-created"
export const norm = (t?: string) =>
  String(t || "")
    .replace(/^bingo[-_.]/, "bingo:")
    .replace(/session[-_.]/, "session-")
    .replace(/winner[-_.]announced$/, "winner-announced");

// 12 line definitions (5 rows, 5 cols, 2 diagonals) over a 5x5 grid (0..24)
export const LINE_DEFS: number[][] = [
  // rows
  [0, 1, 2, 3, 4],
  [5, 6, 7, 8, 9],
  [10, 11, 12, 13, 14],
  [15, 16, 17, 18, 19],
  [20, 21, 22, 23, 24],
  // cols
  [0, 5, 10, 15, 20],
  [1, 6, 11, 16, 21],
  [2, 7, 12, 17, 22],
  [3, 8, 13, 18, 23],
  [4, 9, 14, 19, 24],
  // diagonals
  [0, 6, 12, 18, 24],
  [4, 8, 12, 16, 20],
];

// —— Netflix-ish tokens
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  panelHover: "#191919",
  border: "rgba(255,255,255,0.12)",
  text: "#d32828ff",
  muted: "rgba(151, 26, 26, 0.75)",
  red: "#E50914",
  redHover: "#b20710",
  soft: "rgba(255,255,255,0.06)",
};

const StyledButton = styled(Button)(({ theme }) => ({
  position: "relative",
  width: "100%",
  aspectRatio: "1 / 1",                // always square
  minWidth: 0,
  padding: 0,
  overflow: "hidden",                   // prevents glow/focus from overflowing
  fontWeight: 900,
  fontSize: "clamp(0.62rem, 2.9vw, 0.95rem)", // smaller on phones
  borderRadius: 12,
  textTransform: "none",
  backgroundColor: "#141414",
  color: "#fff",
  borderStyle: "solid",
  borderWidth: 1.5,                     // slimmer border to free space
  borderColor: "rgba(255,255,255,0.12)",
  boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
  transition:
    "transform 140ms ease, box-shadow 140ms ease, background-color 140ms ease, border-color 140ms ease",
  "&:hover": {
    transform: "translateY(-1px)",
    backgroundColor: "#191919",
    borderColor: "rgba(255,255,255,0.18)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.45)",
  },
  "&:disabled": { opacity: 0.55, filter: "saturate(0.85)" },

  // Softer effects on small screens to avoid visual crowding
  [theme.breakpoints.down("sm")]: {
    borderRadius: 10,
    "&:hover": { transform: "none" },
  },
}));


/* ------------------------------- Sub-UI ------------------------------- */

export const BingoProgress: React.FC<{ count: number }> = ({ count }) => {
  // shows B I N G O with active ones filled
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      {BINGO.map((ch, idx) => {
        const active = idx < count;
        return (
          <Chip
            key={ch}
            label={ch}
            variant={active ? "filled" : "outlined"}
            color={active ? "error" : "default"}
            sx={{
              fontWeight: 900,
              letterSpacing: 1,
              px: 1.25,
              py: 0.25,
              borderWidth: 2,
              borderColor: active ? COLORS.red : COLORS.border,
              background: active ? COLORS.red : "transparent",
              color: COLORS.text,
              fontSize: { xs: "0.95rem", sm: "1rem" },
              transition: "transform 140ms ease, box-shadow 140ms ease",
              transform: active ? "scale(1.06)" : "scale(1.0)",
              boxShadow: active ? "0 8px 18px rgba(229,9,20,0.35)" : "none",
            }}
          />
        );
      })}
    </Stack>
  );
};

export const BingoCellButton: React.FC<{
  cell: BingoCell;
  isCurrent: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ cell, isCurrent, disabled, onClick }) => {
  const theme = useTheme();
  return (
    <Fade in timeout={220}>
      <StyledButton
        variant={cell.isClicked ? "contained" : "outlined"}
        color={isCurrent ? "error" : "primary"}
        onClick={onClick}
        disabled={disabled}
        aria-pressed={cell.isClicked}
        aria-label={`Cell ${cell.number}${cell.isClicked ? " marked" : ""}${isCurrent ? ", current number" : ""}`}
        sx={{
          // slightly smaller text on phones
          fontSize: { xs: "clamp(0.6rem, 2.6vw, 0.82rem)", sm: undefined },
          "-webkit-tap-highlight-color": "transparent",

          ...(cell.isClicked && {
            background: `linear-gradient(180deg, ${COLORS.red}, ${COLORS.redHover})`,
            borderColor: COLORS.red,
            color: "#fff",
            boxShadow: "0 12px 28px rgba(6, 5, 5, 0.32)",
            transform: { xs: "none", sm: "translateY(-1px)" }, // no nudge on phones
          }),

          ...(isCurrent && !cell.isClicked && {
            boxShadow: "0 8px 22px rgba(229,9,20,0.22), 0 0 0 2px rgba(229,9,20,0.55) inset",
            transform: { xs: "none", sm: "scale(1.03)" },       // no scale on phones
            borderColor: COLORS.red,
          }),
        }}
      >
        {cell.number}
      </StyledButton>
    </Fade>
  );
};



// ——— replace your BingoBoard with this
export const BingoBoard: React.FC<{
  board: BingoCell[];
  currentNumber: number | null;
  onCellClick: (index: number) => void;
  marking: boolean;
}> = ({ board, currentNumber, onCellClick, marking }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",          // always 5 columns
        gap: { xs: "clamp(4px, 1.4vw, 8px)", sm: "clamp(6px, 1vw, 10px)" }, // clean spacing on phones
        p: { xs: 0.75, sm: 1 },
        borderRadius: 5,
        border: "1px solid rgba(255,255,255,0.12)",
        background:
          "radial-gradient(800px 380px at 10% 0%, rgba(229,9,20,0.08), transparent 60%), #0b0b0b",
        boxSizing: "border-box",
        maxWidth: "min(94vw, 560px)",                   // keeps board from hugging edges
        marginInline: "auto",
      }}
    >
      {board.map((cell, idx) => (
        <Box key={idx}>
          <BingoCellButton
            cell={cell}
            isCurrent={cell.number === currentNumber}
            disabled={cell.isClicked || marking}
            onClick={() => onCellClick(idx)}
          />
        </Box>
      ))}
    </Box>
  );
};


export const BingoStatus: React.FC<{
  currentNumber: number | null;
  allCalled: number[];
  linesCompleted: number;
}> = ({ currentNumber, allCalled, linesCompleted }) => {
  return (
    <Stack spacing={1.5} mt={3} alignItems="center" sx={{ color: COLORS.text }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, color: COLORS.text, textShadow: "0 1px 0 rgba(0,0,0,0.35)" }}
      >
        Called Number:{" "}
        <Box component="span" sx={{ color: COLORS.red, fontWeight: 900 }}>
          {currentNumber ?? "Waiting..."}
        </Box>
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: COLORS.muted, textAlign: "center", maxWidth: 680, lineHeight: 1.6 }}
      >
        <strong style={{ color: COLORS.text }}>All Called:</strong>{" "}
        {allCalled.length ? allCalled.join(", ") : "None yet"}
      </Typography>

      <Badge
        color="error"
        badgeContent={linesCompleted}
        max={5}
        sx={{
          "& .MuiBadge-badge": {
            fontWeight: 900,
            fontSize: "0.8rem",
            backgroundColor: COLORS.red,
            boxShadow: "0 6px 16px rgba(229,9,20,0.35)",
          },
        }} 
      >
        <Chip
          label={`${linesCompleted} / 5 lines`}
          variant="outlined"
          color="error"
          sx={{
            fontWeight: 800,
            borderWidth: 2,
            borderColor: COLORS.red,
            color: COLORS.text,
            backgroundColor: COLORS.soft,
          }}
        />
      </Badge>
    </Stack>
  );
};
